/**
 * ForgotPassword
 *
 * Public page: user enters email, receives reset link (via PlatformSettings
 * SMTP if configured).  We always show a success message regardless of
 * whether the email exists -- no account enumeration.
 */
import * as React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { api, extractApiError } from "../../lib/api";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Card, CardBody, CardHeader } from "../../components/ui/Card";

export default function ForgotPassword() {
  const [email, setEmail] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-12 md:py-20">
      <Link
        to="/login"
        className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink mb-4 font-semibold"
        data-testid="forgot-back-to-login"
      >
        <ArrowLeft className="h-4 w-4" /> Back to sign in
      </Link>
      <Card elevated>
        <CardHeader>
          <div>
            <h1 className="text-2xl font-black tracking-tight">Forgot your password?</h1>
            <p className="text-ink-muted text-sm mt-1">
              Enter your email and we'll send you a reset link valid for 30 minutes.
            </p>
          </div>
        </CardHeader>
        <CardBody>
          {sent ? (
            <div
              data-testid="forgot-sent-ok"
              className="rounded-2xl border-2 border-ink bg-surface-sunken p-5 text-center"
            >
              <Mail className="h-8 w-8 text-brand mx-auto mb-2" aria-hidden />
              <h2 className="text-lg font-bold">Check your inbox</h2>
              <p className="text-ink-muted text-sm mt-1">
                If an account exists for <strong>{email}</strong>, a reset link is on its way.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-4" data-testid="forgot-form">
              <Input
                label="Email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="forgot-email"
              />
              {error && (
                <p
                  data-testid="forgot-error"
                  role="alert"
                  className="text-sm font-semibold text-brand"
                >
                  {error}
                </p>
              )}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
                data-testid="forgot-submit"
              >
                Send reset link
              </Button>
            </form>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
